import type {Dispatch, SetStateAction} from 'react';
import type {ApolloError} from '@shopify/react-graphql';
import {useMutation} from '@shopify/react-graphql';
import {useI18n} from '@shopify/react-i18n';
import type {Image} from '@web-utilities/types';
import type {
  ApolloQueryResult,
  OperationVariables,
  DataProxy,
  FetchResult,
} from '@apollo/client';

import {MediaUploadStatus} from '../components';
import type {MediaUploadResult} from '../components';
import {DEFAULT_IMAGES, DEFAULT_QUERY} from '../constants';
import type {AddShopImageMutationData} from '../graphql/AddShopImageMutation.graphql';
import addShopImageMutation from '../graphql/AddShopImageMutation.graphql';
import type {InsertImageModalQueryData} from '../graphql/InsertImageModalQuery.graphql';
import insertImageModalQuery from '../graphql/InsertImageModalQuery.graphql';

interface UploadImageFromUrlProps {
  url: string;
  loading: boolean;
  setValidationError: Dispatch<SetStateAction<string | null>>;
  refetch: (
    variables?: OperationVariables | undefined,
  ) => Promise<ApolloQueryResult<InsertImageModalQueryData>>;
  onInsertImage?: (image: Image) => void;
}

interface UploadImageProps {
  result: MediaUploadResult;
  loading: boolean;
  hasPreviousPage: boolean;
  refetch: (
    variables?: OperationVariables | undefined,
  ) => Promise<ApolloQueryResult<InsertImageModalQueryData>>;
  error?: ApolloError;
}

export const useAddShopImageMutation = (
  handleToast: (content: string, error?: boolean) => void,
) => {
  const [i18n] = useI18n();
  const addImageMutation = useMutation(addShopImageMutation);

  const handleUploadImageFromUrl = async ({
    url,
    loading,
    setValidationError,
    refetch,
    onInsertImage,
  }: UploadImageFromUrlProps) => {
    if (loading || onInsertImage == null) {
      handleToast(i18n.translate('Common.serverError'), true);
      return;
    }

    try {
      const mutationData = await addImageMutation({
        variables: {
          image: {
            src: url,
          },
        },
      });

      const shopImageUpload = mutationData.data?.shopImageUpload;
      if (shopImageUpload == null || shopImageUpload.image == null) {
        handleToast(i18n.translate('Common.addImageErrorMessage'), true);
      } else if (shopImageUpload.userErrors.length > 0) {
        setValidationError(shopImageUpload.userErrors[0].message);
      } else {
        handleToast(
          i18n.translate('Common.addImageSuccessMessage', {
            count: 1,
          }),
        );
        const newImage: Image = {
          src: shopImageUpload.image.src,
        };
        onInsertImage(newImage);
        refetch(DEFAULT_QUERY);
      }
    } catch (err) {
      handleToast(i18n.translate('Common.serverError'), true);
    }
  };

  const handleUploadImage = async ({
    result,
    loading,
    hasPreviousPage,
    refetch,
    error,
  }: UploadImageProps) => {
    if (
      result.status === MediaUploadStatus.ValidationFailed ||
      result.status === MediaUploadStatus.FinalUploadFailed ||
      result.status === MediaUploadStatus.StagedUploadFailed ||
      loading ||
      error
    ) {
      handleToast(i18n.translate('Common.serverError'), true);
      return;
    }

    const update = hasPreviousPage
      ? undefined
      : updateShopImageAfterAddShopImageMutation;
    try {
      const mutationData = await addImageMutation({
        update,
        variables: {
          image: {
            src: result.path,
          },
        },
      });

      const shopImageUpload = mutationData.data?.shopImageUpload;

      if (!shopImageUpload) {
        handleToast(i18n.translate('Common.addImageErrorMessage'), true);
      } else if (shopImageUpload.userErrors.length > 0) {
        handleToast(shopImageUpload.userErrors[0].message, true);
      } else {
        if (hasPreviousPage) {
          refetch(DEFAULT_QUERY);
        }
        handleToast(
          i18n.translate('Common.addImageSuccessMessage', {
            count: 1,
          }),
        );
      }
    } catch (err) {
      handleToast(i18n.translate('Common.serverError'), true);
    }
  };

  const updateShopImageAfterAddShopImageMutation = (
    dataProxy: DataProxy,
    fetchResult: FetchResult<AddShopImageMutationData>,
  ) => {
    const dataFromCache = dataProxy.readQuery<InsertImageModalQueryData>({
      query: insertImageModalQuery,
      variables: DEFAULT_QUERY,
    });

    let newCacheData: InsertImageModalQueryData | null = dataFromCache;

    const data = fetchResult.data;
    if (
      newCacheData &&
      data &&
      data.shopImageUpload &&
      data.shopImageUpload.image != null
    ) {
      const newImage = data.shopImageUpload.image;
      const {edges} = newCacheData.shop.uploadedImages;
      const latestCursor = edges.length > 0 ? edges[0].cursor : null;
      const typename = edges.length > 0 ? edges[0].__typename : 'ImageEdge';
      const newImageAsEdges = {
        __typename: typename,
        node: newImage,
        cursor: latestCursor as string,
      };

      if (edges.length === DEFAULT_IMAGES) {
        const newEdges = edges.slice(0, edges.length - 1);

        newCacheData = {
          ...newCacheData,
          shop: {
            ...newCacheData.shop,
            uploadedImages: {
              ...newCacheData.shop.uploadedImages,
              pageInfo: {
                ...newCacheData.shop.uploadedImages.pageInfo,
                hasNextPage: true,
              },
              edges: [...newEdges],
            },
          },
        };
      }

      const unshiftedEdges = [
        newImageAsEdges,
        ...newCacheData.shop.uploadedImages.edges,
      ];

      newCacheData = {
        ...newCacheData,
        shop: {
          ...newCacheData.shop,
          uploadedImages: {
            ...newCacheData.shop.uploadedImages,
            edges: [...unshiftedEdges],
          },
        },
      };
    }

    dataProxy.writeQuery({
      query: insertImageModalQuery,
      variables: DEFAULT_QUERY,
      data: newCacheData,
    });
  };

  return {handleUploadImageFromUrl, handleUploadImage};
};
